import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthLinks } from "@/components/Billing";
import {
  DEFAULT_LOGIN_COPY,
  normaliseLoginCopy,
  splashImagesWithUploads,
} from "@/lib/loginScreen";

export function GoogleButton({
  before,
  label = "Continue with Google",
  testId = "google-login-button",
}) {
  const go = () => {
    if (before && !before()) return;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/chat";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      type="button"
      variant="outline"
      className="w-full h-11"
      onClick={go}
      data-testid={testId}
    >
      <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48">
        <path
          fill="#FFC107"
          d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
        />
        <path
          fill="#FF3D00"
          d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
        />
      </svg>
      {label}
    </Button>
  );
}

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copy, setCopy] = useState(DEFAULT_LOGIN_COPY);
  const [splashId, setSplashId] = useState("");
  const [splashLoaded, setSplashLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/public/login-screen")
      .then(({ data }) => {
        const updatedCopy = normaliseLoginCopy(data);
        setCopy(updatedCopy);
        const choices = updatedCopy.splash_image_ids;
        const imageId = updatedCopy.splash_mode === "static"
          ? choices[0]
          : choices[Math.floor(Math.random() * choices.length)];
        setSplashId(imageId);
      })
      .catch(() => setSplashId(DEFAULT_LOGIN_COPY.splash_image_ids[0]));
  }, []);

  const splashImages = splashImagesWithUploads(copy.custom_images);
  const splashImage = splashId
    ? splashImages.find((image) => image.id === splashId) || splashImages[0]
    : null;

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(data.role === "technician" ? "/prep" : "/chat");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden bg-slate-900">
        {splashImage ? (
          <img
            key={splashImage.id}
            src={splashImage.url}
            alt={splashImage.alt}
            onLoad={() => setSplashLoaded(true)}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
              splashLoaded ? "opacity-60" : "opacity-0"
            }`}
            data-testid="login-splash-image"
          />
        ) : (
          <div
            className="absolute inset-0 bg-slate-900"
            data-testid="login-splash-loading"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white max-w-xl">
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-4">
            {copy.hero_eyebrow}
          </p>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight">
            {copy.hero_title}
          </h1>
          <p className="mt-4 text-slate-300 text-base">
            {copy.hero_body}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 lab-grid">
        <div className="w-full max-w-sm bg-card border rounded-2xl p-8 shadow-sm rise">
          <div className="mb-8 flex items-center gap-3">
            <div className="grid h-16 w-16 place-items-center rounded-2xl bg-primary text-primary-foreground">
              <img
                src="/askjonnylogosmall.png"
                alt="Ask Jonny"
                className="h-full w-full rounded-2xl bg-white object-contain"
                data-testid="login-brand-logo"
              />
            </div>
            <div>
              <div
                className="text-xl font-bold leading-none"
                data-testid="login-brand"
              >
                {copy.brand_name}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {copy.school_sign_in}
              </div>
            </div>
          </div>
          <form
            onSubmit={submit}
            className="space-y-4"
            data-testid="login-form"
          >
            <div className="space-y-1.5">
              <Label htmlFor="email">{copy.email_label}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{copy.password_label}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="w-full h-11"
              disabled={busy}
              data-testid="login-submit-button"
            >
              {busy ? copy.signing_in_label : copy.sign_in_label}{" "}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </form>
          <AuthLinks
            forgotLabel={copy.forgot_password_label}
            registerLabel={copy.register_school_label}
          />
          <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground">
            <div className="h-px bg-border flex-1" />
            {copy.divider_label}
            <div className="h-px bg-border flex-1" />
          </div>
          <GoogleButton
            label={copy.google_label}
            before={() => {
              sessionStorage.removeItem("aj-signup-school");
              sessionStorage.removeItem("aj-invite-token");
              return true;
            }}
          />
          <p className="text-xs text-muted-foreground mt-6">
            {copy.google_help} {copy.signup_prefix}{" "}
            <a href="/signup" className="text-primary hover:underline">
              {copy.signup_link_label}
            </a>
            .
          </p>
          <p
            className="mt-4 border-l-2 border-primary/40 pl-3 text-xs leading-5 text-muted-foreground"
            data-testid="login-manual-reassurance"
          >
            {copy.manual_reassurance}
          </p>
          <Link
            className="mt-4 inline-flex text-xs font-medium text-primary transition-colors hover:underline"
            to="/faq"
            data-testid="login-faq-link"
          >
            {copy.faq_label}
          </Link>
        </div>
      </div>
    </div>
  );
}
